import type { UseQueryOptions } from '@tanstack/react-query';
import type { inferRouterInputs, inferRouterOutputs } from '@trpc/server';
import type { Override } from 'config-hub-server/src/domain/overrides.js';
import type { AppRouter } from 'config-hub-server/src/router/index.js';
import type { AdoptResult } from 'config-hub-server/src/service/adopt.js';
import type { EntityType } from '../domain/entityTypes.js';
import { newGameRoute, splitGameKey } from '../domain/routes.js';
import type { useTRPC, useTRPCClient } from '../utils/trpc.js';

type Trpc = ReturnType<typeof useTRPC>;
type Client = ReturnType<typeof useTRPCClient>;
type Inputs = inferRouterInputs<AppRouter>;
type Outputs = inferRouterOutputs<AppRouter>;
export type EntityDetail = Outputs['games']['get'];
export type RootRow = NonNullable<Outputs['studios']['get']['row']>;
export type AdoptToBaseResult = Outputs['games']['adoptToBase'];
export type DeleteMasterResult = Outputs['studios']['delete'];
/** **Adopt to master**: one field of one record, from one environment, into master's base — `id`/`key` normalised to one `key` across types. */
export type AdoptToBaseRequest = Omit<Inputs['studios']['adoptToBase'], 'id'> & { key: string };

/**
 * The per-type tRPC calls, keyed once. `domain/entityTypes.ts` holds everything that does not
 * need the wire; this holds what does, so a hook reads `ENTITY_CALLS[type]` instead of
 * branching on the type name.
 */

/** The drawer's detail and, for a parent type, its grid row — what `detailQuery`'s `select` always shapes its data into. */
export type EntityDetailData = { detail: EntityDetail | undefined; row: RootRow | null };

/** Named records of one type, adopted from one environment (the inbox, the selection bar, a drift row's **Adopt**). */
export type AdoptRecordsInput = { environment: string; keys: string[]; fields?: string[] };

/** What the editor's save writes, `key` normalised like `AdoptToBaseRequest`'s. */
export type SaveRecordInput = {
    key: string;
    targets: string;
    base: Record<string, unknown>;
    overrides: Override[];
    expectedRevision?: number;
};

/** A save that also changes the record's key: `key` is the new key, `fromKey` the one it leaves. */
export type MoveRecordInput = SaveRecordInput & { fromKey: string; expectedRevision: number };

export type EntityCalls = {
    detailQuery: (trpc: Trpc, key: string, focusEnvironment: string | undefined) => UseQueryOptions<EntityDetailData>;
    deleteMaster: (client: Client, key: string, expectedRevision: number) => Promise<DeleteMasterResult>;
    /** For a parent type, the drawer header's shortcut to create a child under the record. */
    addChild?: { label: string; route: (key: string) => string };
    /** Named records master already has keep this environment's values as their own; new ones are copied in. */
    adopt: (client: Client, request: AdoptRecordsInput) => Promise<AdoptResult>;
    /** **Adopt to master**, planned: a query, no audit row — what the new base does to every environment. */
    adoptToBasePreviewOptions: (trpc: Trpc, request: AdoptToBaseRequest) => UseQueryOptions<AdoptToBaseResult>;
    /** **Adopt to master**, written. Maps `request`'s `key` onto the wire shape, like `adopt`/`deleteMaster`. */
    adoptToBase: (client: Client, request: AdoptToBaseRequest) => Promise<AdoptToBaseResult>;
    /** The master editor's save (`useMasterEditor`'s one mutation), `key` mapped onto the wire like `adopt`/`deleteMaster`. */
    save: (client: Client, request: SaveRecordInput) => Promise<unknown>;
    /** A save that gives the record another parent (a game, another studio); absent for types whose key cannot change. */
    move?: (client: Client, request: MoveRecordInput) => Promise<unknown>;
    /** The create-time id availability check: the record at `key`, or nothing found. */
    lookup: (client: Client, key: string) => Promise<{ master: unknown } | null | undefined>;
};

const studioWireKey = (key: string) => ({ id: key });
const gameWireKey = (key: string) => ({ key: splitGameKey(key) });
const configWireKey = (key: string) => ({ name: key });

export const ENTITY_CALLS: Record<EntityType, EntityCalls> = {
    studio: {
        detailQuery: (trpc, key, focusEnvironment) =>
            trpc.studios.get.queryOptions(
                { ...studioWireKey(key), focusEnvironment },
                { select: (data) => ({ detail: data.detail, row: data.row ?? null }) },
            ) as never,
        deleteMaster: (client, key, expectedRevision) =>
            client.studios.delete.mutate({ ...studioWireKey(key), expectedRevision }),
        addChild: { label: 'Add game', route: (key) => newGameRoute(key) },
        adopt: (client, { environment, keys, fields }) =>
            client.studios.adopt.mutate({ environment, existing: 'environment', ids: keys, fields }),
        adoptToBasePreviewOptions: (trpc, { key, ...rest }) =>
            trpc.studios.adoptToBasePreview.queryOptions({ ...studioWireKey(key), ...rest }) as never,
        adoptToBase: (client, { key, ...rest }) =>
            client.studios.adoptToBase.mutate({ ...studioWireKey(key), ...rest }),
        save: (client, { key, ...rest }) => client.studios.save.mutate({ ...studioWireKey(key), ...rest }),
        lookup: async (client, key) => (await client.studios.get.query(studioWireKey(key))).detail,
    },
    game: {
        detailQuery: (trpc, key, focusEnvironment) =>
            trpc.games.get.queryOptions(
                { ...gameWireKey(key || '/'), focusEnvironment },
                { select: (data) => ({ detail: data, row: null }) },
            ) as never,
        deleteMaster: (client, key, expectedRevision) =>
            client.games.delete.mutate({ ...gameWireKey(key), expectedRevision }),
        adopt: (client, { environment, keys, fields }) =>
            client.games.adopt.mutate({ environment, existing: 'environment', keys: keys.map(splitGameKey), fields }),
        adoptToBasePreviewOptions: (trpc, { key, ...rest }) =>
            trpc.games.adoptToBasePreview.queryOptions({ ...gameWireKey(key), ...rest }) as never,
        adoptToBase: (client, { key, ...rest }) => client.games.adoptToBase.mutate({ ...gameWireKey(key), ...rest }),
        save: (client, { key, ...rest }) => client.games.save.mutate({ ...gameWireKey(key), ...rest }),
        move: (client, { fromKey, key: _key, ...rest }) =>
            client.games.move.mutate({ ...gameWireKey(fromKey), ...rest }),
        lookup: (client, key) => client.games.get.query(gameWireKey(key || '/')),
    },
    config: {
        detailQuery: (trpc, key, focusEnvironment) =>
            trpc.configs.get.queryOptions(
                { ...configWireKey(key), focusEnvironment },
                { select: (data) => ({ detail: data.detail, row: data.row ?? null }) },
            ) as never,
        deleteMaster: (client, key, expectedRevision) =>
            client.configs.delete.mutate({ ...configWireKey(key), expectedRevision }),
        adopt: (client, { environment, keys, fields }) =>
            client.configs.adopt.mutate({ environment, existing: 'environment', names: keys, fields }),
        adoptToBasePreviewOptions: (trpc, { key, ...rest }) =>
            trpc.configs.adoptToBasePreview.queryOptions({ ...configWireKey(key), ...rest }) as never,
        adoptToBase: (client, { key, ...rest }) =>
            client.configs.adoptToBase.mutate({ ...configWireKey(key), ...rest }),
        save: (client, { key, ...rest }) => client.configs.save.mutate({ ...configWireKey(key), ...rest }),
        lookup: async (client, key) => (await client.configs.get.query(configWireKey(key))).detail,
    },
};
